import {QuotationUserSelectorProps} from "./types";
import {UserOption} from "@feature/commons";
import {FormattedMessage, useIntl} from "react-intl";
import {CheckIcon, XMarkIcon, MinusCircleIcon, EnvelopeIcon} from "@heroicons/react/24/outline";
import {CheckCircleIcon, PencilSquareIcon} from "@heroicons/react/24/solid";
import {useEffect, useState} from "react";
import SearchSelect from "@ui-components/SearchSelect";
import IconButton from "@ui-components/IconButton";
import Modal from "@ui-components/Modal";
import {QuotationEnterpriseSelector} from "../quotation-enterprise-selector";
import {Tooltip} from "react-tippy";
import {isValidEnterprise} from "@feature/quotations/lib/utilities";

export const QuotationUserSelector = ({
  editable,
  label,
  value,
  fixed,
  fetch,
  onSelect,
  submittingSelection,
  onInvite,
  submittingInvitation,
  userKey,
  defaultOptions,
}: QuotationUserSelectorProps) => {

  // hooks
  const intl = useIntl();

  // state
  const [selected, setSelected]
    = useState<UserOption | undefined>(undefined);
  const [mode, setMode]
    = useState<'display' | 'edit'>('display');
  const [inviteModelOpen, setInviteModelOpen] = useState(false);

  // utilities
  const userBadges = {
    enabled: {
      Icon: CheckCircleIcon,
      label: <FormattedMessage id="enabled" defaultMessage="Abilitato"/>,
      color: "text-kerakoll-700 font-semibold",
    },
    waiting: {
      Icon: MinusCircleIcon,
      label: <FormattedMessage id="waiting_for_enablement" defaultMessage="In attesa di abilitazione"/>,
      color: "text-gray-400",
    },
  };
  const currentBadge = fixed ? userBadges.enabled : userBadges.waiting;
  const showEnable = !fixed && editable && mode === 'display' && !!value?.label;
  const canEnable = showEnable && !!value?.value;
  const enableBotton = <span
    className={
      `${!submittingInvitation && canEnable ? "bg-kerakoll-700 hover:bg-kerakoll-400 cursor-pointer" : "bg-kerakoll-400 pointer-events-none cursor-not-allowed"} text-white  text-xs font-medium px-2 py-1 rounded mt-0.5 flex flex-row gap-1 items-center relative`
    }
    onClick={() => {
      if (!submittingInvitation)
        setInviteModelOpen(true);
    }}
  >
    <CheckIcon className="w-3"/>
    <FormattedMessage id="enable" defaultMessage="Abilita"/>
  </span>;

  useEffect(() => {
    setSelected(editable && !!value?.value ? value : undefined);
  }, [editable]);

  return <>
    <div className="flex flex-col gap-1">
      <div className="flex flex-row gap-2 items-center">
        <div className="font-medium">
          <FormattedMessage id={label}/>
        </div>
        <div className="flex justify-between items-center gap-6 w-full">
          {/* ---- USER TYPE LABEL ----*/}
          <div className={`flex flex-row gap-1 items-center ${currentBadge.color}`}>
            <currentBadge.Icon className="w-5 mt-1"/>
            <div className="text-xs mt-0.5">{currentBadge.label}</div>
          </div>
          {/* ---- INVITATION BUTTON ----*/}
          {showEnable && (
            canEnable? enableBotton : <Tooltip title={intl.formatMessage({
              id: "user_not_registered_yet",
              defaultMessage: "Utente non ancora registrato",
            })} arrow>
              {enableBotton}
            </Tooltip>
          )}
        </div>
      </div>
      <div className="flex flex-row gap-2">
        {
          mode === 'edit'
            // -------- USER SELECTION MODE --------
            ? (
              <div className="flex flex-row gap-2 items-center w-full">
                <div className="flex-1">
                  {/*@ts-ignore*/}
                  {userKey !== 'enterprise' ?
                    <SearchSelect
                      value={selected}
                      loadOptions={fetch}
                      onChange={(value: UserOption) => {
                        setSelected(value);
                      }}
                      isClearable={true}
                      isDisabled={submittingSelection}
                      noOptionsMessage={() => intl.formatMessage({
                        id: "type_value",
                        defaultMessage: "Digita un valore",
                      })}
                      defaultOptions={defaultOptions}
                      CustomOption={userKey==='resale'?({data: userOption, innerProps, innerRef}: any) => {
                        const {object, label, secondaryLabel} = userOption;
                        return (
                          <article
                            ref={innerRef}
                            className="py-2 px-3 cursor-pointer hover:bg-blue-50"
                            {...innerProps}
                          >
                            {!!object && <p className="font-semibold text-sm">{label}</p>}
                            <p className={!object ? "text-sm" : "text-xs"}>
                              {secondaryLabel}
                            </p>
                          </article>
                        )
                      }:undefined}
                    /> : <QuotationEnterpriseSelector
                      value={selected}
                      loadOptions={fetch}
                      onChange={(value: UserOption) => {
                        setSelected(value);
                      }}
                      isDisabled={submittingSelection}
                      noOptionsMessage={() => intl.formatMessage({
                        id: "type_value",
                        defaultMessage: "Digita un valore",
                      })}
                      defaultOptions={defaultOptions}
                    />}
                </div>
                <div className="mb-2">
                  <IconButton
                    styleType="greenWhite"
                    onClick={() => {
                      onSelect(selected);
                      setMode('display');
                    }}
                    disabled={userKey==='enterprise' && !isValidEnterprise(selected)}
                    Icon={CheckIcon}
                    submitting={submittingSelection}
                  />
                </div>
                <div className="mb-2">
                  <IconButton
                    styleType="redWhite"
                    onClick={() => setMode('display')}
                    Icon={XMarkIcon}
                    submitting={submittingSelection}
                  />
                </div>
              </div>
            )
            // -------- USER DISPLAY MODE --------
            : (
              <div className="flex flex-row gap-2 items-center">
                <span>
                  {
                    !!value?.label
                      ? value.label
                      : <div className="text-gray-400">
                        <FormattedMessage id="not_set" defaultMessage="Non impostato"/>
                      </div>
                  }
                </span>
                {!fixed && editable && (
                  <PencilSquareIcon
                    className="w-4 cursor-pointer mt-0.5"
                    onClick={() => setMode('edit')}
                  />
                )}
              </div>
            )
        }
      </div>
    </div>
    {inviteModelOpen && (
      <Modal
        title={
          <FormattedMessage
            id="quotation_user_invite"
            defaultMessage="Invita utente"
          />
        }
        description={
          <FormattedMessage
            id="quotation_user_invite_description"
            defaultMessage={
              "L'utente selezionato avrà accesso a questo preventivo, del quale vedrà solamente le parti a lui " +
              "condivise. Questa operazione non può essere annullata. Vuoi procedere?"
            }
          />
        }
        children={undefined}
        onDismiss={() => setInviteModelOpen(false)}
        onConfirm={async () => {
          await onInvite();
          setInviteModelOpen(false);
        }}
        confirmText={intl.formatMessage({id: 'confirm'})}
        onExit={() => setInviteModelOpen(false)}
        submitting={submittingInvitation}
      />
    )}
  </>
}